// keycloak-js 26 (aligned with the Keycloak 26 server) validates the nonce on the ID token only.
// keycloak-js 21 checked it on the ACCESS token, which Keycloak 26 no longer stamps with a nonce ->
// "Invalid nonce, clearing token" -> init rejected with `undefined` -> signed out after a good login.
// The package is ESM-only and resolves through its `exports` map, which webpack 4 (react-scripts 3)
// ignores (no `main` field): import the module file directly.
import Keycloak from 'keycloak-js/lib/keycloak.js';
import { env } from '../config/runtimeEnv';

// "Login with Keycloak" — Authorization Code + PKCE against the dedicated showroom realm.
// The client is PUBLIC (no secret): the browser exchanges the auth code with PKCE, so
// nothing confidential ever reaches the front. This is the SEC-04-safe login mode.
//
// Config values are PUBLIC (no secret) and baked at build time (like the rest of the
// app). Override at build via REACT_APP_* if needed.
// End-user login realm = `showroom-realm`, PUBLIC client `showroom-spa` (provisioned in prod):
// publicClient=true, standardFlow + PKCE S256, QR-first browser flow bound (renders the HIA QR).
// This is the SEC-04 fix — no secret in the front. Do NOT use `tenant-realm/showroom`
// (that's an M2M service client, not for end-user login).
// NOTE: Keycloak is under the `/admin` relative path (KC26 KC_HTTP_RELATIVE_PATH=/admin),
// so the base URL MUST include `/admin` — without it keycloak-js hits /realms/... → 404.
// Runtime-first (window.__ENV__ injected by the BFF), with REACT_APP_* build-time and
// hard-coded prod defaults as fallbacks — see src/config/runtimeEnv.js.
const url = env('KEYCLOAK_URL', 'https://prod.hiasecure.com/admin');
const realm = env('SHOWROOM_REALM', 'showroom-realm');
const clientId = env('SHOWROOM_SPA_CLIENT_ID', 'showroom-spa');

const keycloak = new Keycloak({ url, realm, clientId });

let initPromise = null;

// Upper bound of the silent check-sso. keycloak-js applies no timeout to the silent iframe: if it never
// answers (e.g. /silent-check-sso.html refused in a frame, PM-478) init would never settle and the app
// would wait forever. Past this delay the user is treated as signed out (fail closed).
export const SSO_INIT_TIMEOUT_MS = 10000;

const withTimeout = (promise, ms) =>
    new Promise((resolve) => {
        const timer = setTimeout(() => {
            console.error('Keycloak init timed out after ' + ms + ' ms');
            resolve(false);
        }, ms);
        promise.then(
            (value) => { clearTimeout(timer); resolve(value); },
            (error) => { clearTimeout(timer); console.error('Keycloak init failed', error); resolve(false); },
        );
    });

// Silent SSO check on boot: resolves to true if a Keycloak session already exists
// (e.g. right after the redirect back from the login page, or a reload while signed in). Never throws,
// always settles (bounded by SSO_INIT_TIMEOUT_MS).
export const initSso = () => {
    if (!initPromise) {
        initPromise = withTimeout(
            keycloak.init({
                onLoad: 'check-sso',
                pkceMethod: 'S256',
                silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
                checkLoginIframe: false,
            }),
            SSO_INIT_TIMEOUT_MS,
        ).then((authenticated) => authenticated === true);
    }
    return initPromise;
};

// True only when keycloak-js really holds a session (successful init + token), never from storage.
export const isAuthenticated = () => !!(keycloak.authenticated && keycloak.token);

// Redirect to the Keycloak login page. On success Keycloak returns to /admin.
// keycloak-js 26 builds the URL asynchronously (PKCE challenge via WebCrypto): never let it reject silently.
export const login = () =>
    keycloak
        .login({ redirectUri: window.location.origin + '/admin' })
        .catch((error) => console.error('Keycloak login failed', error));

// Closes the SSO session of the realm (end_session_endpoint with id_token_hint +
// post_logout_redirect_uri), then back to the home page. Without an ID token keycloak-js would call
// end-session without id_token_hint and Keycloak would stop on its logout confirmation page: in that case
// there is no Keycloak session known to the app, so just go back to the home page.
export const logout = () => {
    if (keycloak.idToken) {
        return keycloak.logout({ redirectUri: window.location.origin + '/' });
    }
    window.location.assign('/');
    return Promise.resolve();
};

// Name shown in the signed-in workspace, read from the ID/access token claims (never the token).
export const getDisplayName = () => {
    const claims = keycloak.tokenParsed || {};
    return claims.name || claims.preferred_username || '';
};

export default keycloak;
